import React, { useEffect, useState } from "react"
import { EAppView } from "../../Enum/EAppViewType"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { useTranslation } from "react-i18next"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { companyMemory } from "../../Objects/Memory/Memory"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { EStoreServiceType } from "../../Enum/EStoreServiceType"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShareNodes } from "@fortawesome/free-solid-svg-icons"
import { getStoreNow } from "../../utils/timeZone"
import { Bike, BookOpen, ClipboardList, History, Store, type LucideIcon } from "lucide-react"
import { IPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface TopNavProps {
  currentView: EAppView
  setView: (view: EAppView) => void
  mode: EOrderPlaceType
  onToggleMode: () => void
  setIsSocialOpen: (status: boolean) => void
  theme: IPublicMenuTheme
  storeServiceType: EStoreServiceType
}

export const Navbar: React.FC<TopNavProps> = ({
  currentView,
  setView,
  mode,
  onToggleMode,
  setIsSocialOpen,

  theme,
  storeServiceType,
}) => {
  const { auth } = usePublicViewContext()

  const { t } = useTranslation()

  const isStore = mode === EOrderPlaceType.Local

  const isLegacyTheme = isLegacyPublicMenuTheme(theme)

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const now = getStoreNow(companyMemory.timeZoneId)
    const day = now.getDay()

    const schedule = companyMemory.operatingSchedules.find((e) => e.day === day) ?? null

    setIsOpen((schedule ? schedule.isOpen(now) : false) && !companyMemory.isOnVacation(now))
  }, [])

  const NavItem = ({ view, label, Icon }: { view: EAppView; label: string; Icon: LucideIcon }) => {
    const isActive = currentView === view

    return (
      <button
        onClick={() => setView(view)}
        className={`flex flex-col lg:flex-row items-center justify-center gap-1 lg:gap-2 px-4 lg:px-6 py-1 lg:py-2 rounded-[0.5rem] transition-all duration-300 ${
          isActive
            ? "text-orange-600 bg-orange-50 lg:bg-white lg:shadow-md lg:ring-1 lg:ring-black/5"
            : "text-gray-400 hover:text-gray-600"
        }`}
        style={
          isLegacyTheme
            ? undefined
            : {
                backgroundColor: isActive
                  ? theme.mobileNavActiveBackgroundColor
                  : theme.mobileNavInactiveBackgroundColor,
                color: isActive ? theme.mobileNavActiveTextColor : theme.mobileNavInactiveTextColor,
              }
        }
        data-interaction-description-key="interactionDescriptions:publicMenu.components.navbar.button.setViewView"
      >
        <span
          className={`lg:hidden flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
            isActive ? "bg-orange-100 text-orange-700" : "bg-transparent text-gray-500"
          }`}
          style={
            isLegacyTheme
              ? undefined
              : {
                  backgroundColor: isActive
                    ? theme.mobileNavActiveBackgroundColor
                    : theme.mobileNavInactiveBackgroundColor,
                  color: isActive ? theme.mobileNavActiveIconColor : theme.mobileNavInactiveIconColor,
                }
          }
        >
          <Icon className="h-4 w-4" strokeWidth={2.6} />
        </span>

        <span className="text-[10px] lg:text-[11px] font-black uppercase tracking-widest">{label}</span>
      </button>
    )
  }

  return (
    <>
      <nav
        className="sticky top-0 z-50 bg-white backdrop-blur-xl border-b border-gray-100 px-4 md:px-8 py-3 shadow-sm"
        style={
          isLegacyTheme
            ? undefined
            : {
                backgroundColor: `${theme.headerBackgroundColor}`,
                borderColor: theme.borderColor,
                color: theme.textColor,
              }
        }
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {storeServiceType === EStoreServiceType.Both  ? (
              <div
                className="relative bg-gray-100 ring-2 ring-orange-400 p-1 rounded-lg flex items-center shadow-inner border border-gray-200 w-52 sm:w-64 h-11 overflow-hidden"
                style={
                  isLegacyTheme
                    ? undefined
                    : {
                        backgroundColor: theme.serviceSelectorBackgroundColor,
                        color: theme.serviceSelectorTextColor,
                        borderColor: theme.borderColor,
                        boxShadow: `0 0 0 2px ${theme.serviceSelectorActiveBackgroundColor}`,
                      }
                }
              >
                <div
                  className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg transition-all duration-500 ease-out shadow-md ${
                    isStore ? "translate-x-[calc(100%)] bg-green-500" : "translate-x-0 bg-orange-500"
                  }`}
                  style={isLegacyTheme ? undefined : { backgroundColor: theme.serviceSelectorActiveBackgroundColor }}
                />

                <button
                  onClick={() => (!isStore ? null : onToggleMode())}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-2 transition-colors ${
                    !isStore ? "text-black" : "text-black-100"
                  }`}
                  style={
                    isLegacyTheme
                      ? undefined
                      : { color: !isStore ? theme.serviceSelectorActiveTextColor : theme.serviceSelectorTextColor }
                  }
                  data-interaction-description-key="interactionDescriptions:publicMenu.components.navbar.button.isRestaurantNullOnToggleMode"
                >
                  <Bike className="h-4 w-4" strokeWidth={2.5} />
                  <span className="text-[10px] font-black uppercase tracking-wide">
                    {t("publicMenu:navbar.delivery")}
                  </span>
                </button>

                <button
                  onClick={() => (isStore ? null : onToggleMode())}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-2 transition-colors ${
                    isStore ? "text-black" : "text-black-100"
                  }`}
                  style={
                    isLegacyTheme
                      ? undefined
                      : { color: isStore ? theme.serviceSelectorActiveTextColor : theme.serviceSelectorTextColor }
                  }
                  data-interaction-description-key="interactionDescriptions:publicMenu.components.navbar.button.isRestaurantNullOnToggleMode2"
                >
                  <Store className="h-4 w-4" strokeWidth={2.5} />
                  <span className="text-[10px] font-black uppercase tracking-wide">{t("publicMenu:navbar.service_point")}</span>
                </button>
              </div>
            ) : (
              <div
                className="bg-gray-100 ring-2 ring-orange-400 p-1 rounded-lg flex items-center shadow-inner border border-gray-200 h-11 px-5"
                style={
                  isLegacyTheme
                    ? undefined
                    : {
                        backgroundColor: theme.serviceSelectorBackgroundColor,
                        color: theme.serviceSelectorTextColor,
                        borderColor: theme.borderColor,
                        boxShadow: `0 0 0 2px ${theme.serviceSelectorActiveBackgroundColor}`,
                      }
                }
              >
                {isStore ? (
                  <Store className="h-4 w-4 mr-2" strokeWidth={2.5} />
                ) : (
                  <Bike className="h-4 w-4 mr-2" strokeWidth={2.5} />
                )}
                <span
                  className="text-[10px] font-black uppercase tracking-wide text-black"
                  style={isLegacyTheme ? undefined : { color: theme.serviceSelectorTextColor }}
                >
                  {isStore ? t("publicMenu:navbar.service_point") : t("publicMenu:navbar.delivery")}
                </span>
              </div>
            )}

            {!isStore && (
              <div
                className="hidden sm:flex items-center bg-white/60 border border-gray-100 px-3 py-1.5 rounded-lg shadow-sm"
                style={
                  isLegacyTheme
                    ? undefined
                    : {
                        backgroundColor: theme.statusCardBackgroundColor,
                        borderColor: isOpen ? theme.buttonColor : theme.accentColor,
                      }
                }
              >
                <span className="relative flex h-2 w-2 mr-2">
                  <span
                    className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                      isOpen ? "bg-green-400" : "bg-red-400"
                    }`}
                  />
                  <span
                    className={`relative inline-flex rounded-full h-2 w-2 ${isOpen ? "bg-green-500" : "bg-red-500"}`}
                  />
                </span>

                <span
                  className={`text-[9px] font-black uppercase tracking-widest ${
                    isOpen ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {isOpen ? t("publicMenu:navbar.open") : t("publicMenu:navbar.closed")}
                </span>
              </div>
            )}
          </div>

          <div className="hidden lg:flex bg-gray-0 p-1 rounded-lg border border-gray-100">
            <>
                <NavItem view={EAppView.CustomerMenu} label={t("publicMenu:navbar.menu")} Icon={BookOpen} />

                <NavItem
                    view={EAppView.OrderTracking}
                    label={isStore ? t("publicMenu:navbar.command") : t("publicMenu:navbar.orders")}
                    Icon={ClipboardList}
                />

                <NavItem view={EAppView.OrderHistory} label={t("publicMenu:navbar.history")} Icon={History} />
            </>
          </div>

          <div className="flex items-center gap-3">
            {auth.status ? (
              <button
                onClick={() => setView(EAppView.ProfileView)}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-all transform hover:scale-105 ${
                  currentView === EAppView.ProfileView
                    ? "bg-orange-600 text-white shadow-lg shadow-orange-200"
                    : "bg-white ring-2 ring-orange-300 text-orange-600 border border-orange-100"
                }`}
                style={
                  isLegacyTheme
                    ? undefined
                    : {
                        backgroundColor:
                          currentView === EAppView.ProfileView
                            ? theme.loginButtonTextColor
                            : theme.loginButtonBackgroundColor,
                        color:
                          currentView === EAppView.ProfileView
                            ? theme.loginButtonBackgroundColor
                            : theme.loginButtonTextColor,
                        borderColor: theme.loginButtonTextColor,
                        boxShadow: `0 0 0 2px ${theme.loginButtonTextColor}`,
                      }
                }
                data-interaction-description-key="interactionDescriptions:publicMenu.components.navbar.button.setViewEAppViewProfileView"
              >
                {auth.customer?.personEntity.name.charAt(0)}
              </button>
            ) : (
              <button
                onClick={() => setView(EAppView.LoginView)}
                className="px-4 py-2.5 bg-white ring-2 ring-orange-400 text-black rounded-[0.5rem] text-[10px] font-black uppercase tracking-widest hover:#fffdff transition-all shadow-lg"
                style={
                  isLegacyTheme
                    ? undefined
                    : {
                        backgroundColor: theme.loginButtonBackgroundColor,
                        color: theme.loginButtonTextColor,
                        boxShadow: `0 0 0 2px ${theme.loginButtonTextColor}`,
                      }
                }
                data-interaction-description-key="interactionDescriptions:publicMenu.components.navbar.button.setViewEAppViewLoginView"
              >
                {t("publicMenu:login.title")}
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsSocialOpen(true)}
              className="shrink-0 h-9 w-9 rounded-lg ring-2 ring-green-500 bg-orange-50 text-green-700 hover:bg-orange-100"
              aria-label={t("publicMenu:social.title")}
              style={
                isLegacyTheme
                  ? undefined
                  : {
                      backgroundColor: theme.shareButtonBackgroundColor,
                      color: theme.shareButtonTextColor,
                      boxShadow: `0 0 0 2px ${theme.shareButtonTextColor}`,
                    }
              }
              data-interaction-description-key="interactionDescriptions:publicMenu.components.navbar.button.title"
            >
              <FontAwesomeIcon icon={faShareNodes} />
            </button>
          </div>
        </div>
      </nav>

      <div className="lg:hidden fixed inset-x-0 bottom-0 z-50">
        <div
          className="bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] flex justify-around p-1 pt-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] w-full"
          style={
            isLegacyTheme
              ? undefined
              : {
                  backgroundColor: `${theme.mobileNavBackgroundColor}F2`,
                  borderColor: theme.borderColor,
                }
          }
        >
          <NavItem view={EAppView.CustomerMenu} label={t("publicMenu:navbar.menu")} Icon={BookOpen} />

          <NavItem
            view={EAppView.OrderTracking}
            label={isStore ? t("publicMenu:navbar.command") : t("publicMenu:navbar.orders")}
            Icon={ClipboardList}
          />

          <NavItem view={EAppView.OrderHistory} label={t("publicMenu:navbar.history")} Icon={History} />
        </div>
      </div>
    </>
  )
}
